import { useBlocker } from '@tanstack/react-router';

import { Trans, useTranslation } from '@mint-lib/i18n';
import { WarningAltFilled } from '@mint-lib/icons';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogRoot,
  DialogTitle,
  typographyVariants,
} from '@mint-lib/tw-components';

import type { BaseContext } from '../agGridFactory.jsx';
import type { GridApi } from '../enterprise.js';

export type Props<T> = {
  gridApi?: GridApi<T>;

  /**
   * use this prop for pending state of your grid column update
   */
  isUpdatePending?: boolean;
};

export const LeavePageModal = <T extends object = object>({
  gridApi,
  isUpdatePending,
}: Props<T>) => {
  const { t } = useTranslation('@mint-lib/ag-grid');

  const shouldBlockFn = () => {
    const context: BaseContext<T> = gridApi?.getGridOption('context') || {
      checkForGridErrors: () => false,
    };

    return context.checkForGridErrors() || !!isUpdatePending;
  };

  const { proceed, reset, status } = useBlocker({
    shouldBlockFn,
    withResolver: true,
    enableBeforeUnload: shouldBlockFn,
  });

  const handleMessage = () => {
    if (gridApi?.getGridOption('context')?.checkForGridErrors()) {
      return '<Wrapper>If you leave the page now, the cell values with errors will <SecondaryText>revert to their previous state</SecondaryText></Wrapper>';
    }

    if (isUpdatePending) {
      return '<Wrapper>Wait a moment, your changes are being saved...</Wrapper>';
    }

    return '<Wrapper>All is good, we have saved your changes, you can safely leave the page!</Wrapper>';
  };

  return (
    <Dialog modal open={status === 'blocked'}>
      <DialogOverlay />
      <DialogRoot size="s" aria-describedby={undefined}>
        <DialogHeader
          onClose={reset}
          data-dd-action-name="Cancel grid leave page"
        >
          <WarningAltFilled className="mr-2 fill-warning-main" />
          <DialogTitle>{t('Leave this place?')}</DialogTitle>
        </DialogHeader>
        <DialogContent>
          <Trans
            t={t}
            i18nKey={handleMessage()}
            components={{
              Wrapper: (
                <p className={typographyVariants({ variant: 'bodyLong01' })} />
              ),
              SecondaryText: (
                <span
                  className={typographyVariants({ variant: 'bodyLong02' })}
                />
              ),
            }}
          />
        </DialogContent>

        <DialogFooter>
          <Button
            size="large"
            variant="text"
            onClick={reset}
            aria-label="Close leaving modal"
            data-dd-action-name="Cancel grid leave page"
          >
            {t('Cancel')}
          </Button>
          <Button
            size="large"
            aria-label="Leave page"
            onClick={proceed}
            color="warning"
            disabled={isUpdatePending}
            data-dd-action-name="Confirm leave grid page"
          >
            {t('Yes, leave page')}
          </Button>
        </DialogFooter>
      </DialogRoot>
    </Dialog>
  );
};
